import { Context as FDC3Context } from '@finos/fdc3';

import type { z } from 'zod';
import type { contextHookParser, intentResultTypeParser } from './parsers';

export interface CreateOrganization {
  name: string;
  owners: string[];
}

export interface Organization extends CreateOrganization {
  id: string;
}

/**
 * An interface to make entities searchable
 *
 * Intended to be derived from existing entity properties (e.g. name, displayname), and
 * normalized case-wise for search.
 *
 * Not intended to be exposed to front end APIs/hooks
 */
export interface Searchable {
  searchText: string;
}

/**
 * APP: app origin validation
 *
 * OPEN: no app origin validation
 *
 * OPENAUTH: user authentication, no app origin validation
 *
 * STRICT: user authentication and app origin validation
 */
export enum InteropStrategies {
  OPEN = 'open',
  OPENAUTH = 'openauth',
  APP = 'app',
  STRICT = 'strict',
}

export interface UserChannel {
  id: string;
  name?: string;
  color?: string;
}

export interface Lifecycle {
  createdAt: number;
  createdBy?: string;
  updatedAt: number;
  updatedBy?: string;
}

export type ContextHook = z.infer<typeof contextHookParser>;

export interface Directory extends Searchable, Lifecycle {
  name: string;
  description: string;
  icon?: string;
  deliveryHooks?: ContextHook[];
  allowInstanceDiscovery: boolean;
  interopStrategy:
    | InteropStrategies.APP
    | InteropStrategies.OPEN
    | InteropStrategies.OPENAUTH
    | InteropStrategies.STRICT;
  channels: UserChannel[];
  allowGlobalBroadcast: boolean;
  useCustomChannels: boolean;
  generatedFrom?: string;
  assistantPrompt?: string;
  assistantModel?: string;
}

export interface Context extends Searchable, Lifecycle {
  type: string;
  description: string;
  schema?: object;
  details?: string;
  examples?: string;
}

export interface Intent extends Searchable, Lifecycle {
  name: string;
  displayName: string;
  returnContext?: string;
  contexts: Array<string>;
  description?: string;
  examples?: string;
}

export type IntentResultType = z.infer<typeof intentResultTypeParser>;

export type ConnectifiAppMetadata = {
  type: 'window' | 'directory';
  id: string;
  name: string;
  appId: string;
  isSecure: boolean;
  proximity: number;
  title: string;
  instanceTitle: string;
  icons?: { src: string }[];
  lastUpdate?: number;
  os?: string;
  device?: string;
  browser?: string;
};

/**
 * Topics that can be handled by the Connectifi agent.
 */
export enum AgentTopic {
  /**
   * Directs the agent to open a URL.
   */
  Open = 'open',
}

export interface DeliveryHookChange {
  /**
   * The destination to send the changed response to.
   */
  destination: string;

  /**
   *If defined changes the context in the response for the destination.
   */
  context: Context;
}

export interface DeliveryHookRequest {
  context: FDC3Context;
  source: string;
  destinations: string[];
}

export interface DeliveryHookResponse {
  context: FDC3Context | undefined | null;
  changes?: DeliveryHookChange[];
}

export interface OpenHookRequest {
  intent?: string;
  context?: FDC3Context;
  source: string;
  target: string;
}

export interface OpenHookResponse {
  /**
   * Changes the URL location that the Connectifi agent will open.
   */
  url?: string;
}

export interface IntentResultHookRequest {
  intent: string;
  context?: FDC3Context;
  source: string;
  target: string;
}
